import * as React from 'react';
import { actions } from 'react-redux-form';
import {
  Card,
  Button,
  Notification,
  Icon,
  Form,
  ButtonGroup,
  Tooltip,
  IconButton,
  CompanyList,
  DriverList,
  Loader,
} from '../../';
import IContact from './IContact';

interface Props {
  booking: any;
  contact: IContact;
  handleContactSearch: (name: string, email: string, phone: string) => void;
  changeContactType: (string) => void;
  setContact: (any) => void;
  unsetContact: () => void;
  addCompany: (company) => Promise<any>;
  addDriver: (driver) => Promise<any>;
  handleClick: () => Promise<any>;
  handleError: (error: string | boolean) => void;
  isLoading?: boolean;
}

class ContactCollectionForm extends React.Component<Props> {
  private updateContact = (type: string) => () => {
    this.props.changeContactType(type);
    this.props.handleContactSearch(
      this.props.contact.contactName,
      this.props.contact.contactEmail,
      this.props.contact.contactPhone,
    );
  };

  private saveContact = async (contact, booking) => {
    // If a contact type has not been selected don't do anything
    if (!contact.contactType) {
      return false;
    }
    // If the contact exists, we simply assign it to the model
    if (contact.assignedContact) {
      switch (contact.contactType) {
        case 'driver': {
          booking.drivers.push(contact.assignedContact);
          break;
        }
        case 'company': {
          booking.company_id = contact.assignedContact.id;
          break;
        }
      }
    } else {
      // If the contact does not exist, we need to dynamically create one
      if (!contact.contactName.length) {
        return false;
      }
      const payload: any = {
        phone_number: contact.contactPhone ? contact.contactPhone : '',
      };
      switch (contact.contactType) {
        // Drivers require a separate first and last name
        // We do not accept drivers that do not have two supplied
        case 'driver': {
          const names = contact.contactName.split(' ');
          if (names.length < 2) {
            return false;
          }
          payload.first_name = names[0];
          payload.last_name = names[names.length - 1];
          payload.email = contact.contactEmail ? contact.contactEmail : '';
          const { error, data } = await this.props.addDriver(payload);
          if (!error) {
            booking.drivers.push(data);
            this.props.handleError(false);
          } else {
            this.props.handleError(error);
            return false;
          }
          break;
        }
        // Companies consist of a single name
        // We also assign the email to the general contact instead
        case 'company': {
          payload.name = contact.contactName;
          payload.general_contact_email = contact.contactEmail
            ? contact.contactEmail
            : '';
          const { data, error } = await this.props.addCompany(payload);
          if (!error) {
            booking.company_id = data.id;
            this.props.handleError(false);
          } else {
            this.props.handleError(error);
            return false;
          }
          break;
        }
      }
    }

    return true;
  };

  render() {
    return (
      <div>
        <Card list>
          {/* Booking type selection */}
          <label>Who is this booking for?</label>
          <ButtonGroup>
            <IconButton
              className={`is-${
                this.props.contact.contactType === 'driver'
                  ? 'primary'
                  : 'default'
              }`}
              icon="user"
              handleClick={this.updateContact('driver')}
            >
              An Individual
            </IconButton>
            <IconButton
              className={`is-${
                this.props.contact.contactType === 'company'
                  ? 'primary'
                  : 'default'
              }`}
              icon="briefcase"
              handleClick={this.updateContact('company')}
            >
              A Company
            </IconButton>
          </ButtonGroup>

          {/* Name/phone number specification */}
          {!this.props.contact.assignedContact && (
            <div className="field">
              <Form
                fields={[
                  { name: 'name', type: 'text', label: 'Name' },
                  { name: 'phone_number', type: 'text', label: 'Phone Number' },
                  { name: 'email', type: 'text', label: 'Email' },
                ]}
                type="contact"
                hasButton={false}
                handleChange={(data) => {
                  this.props.handleContactSearch(
                    data.name,
                    data.email,
                    data.phone_number,
                  );
                }}
                getDispatch={(dispatch) => {
                  dispatch(
                    actions.change(
                      'contact.name',
                      this.props.contact.contactName,
                    ),
                  );
                  dispatch(
                    actions.change(
                      'contact.phone_number',
                      this.props.contact.contactPhone,
                    ),
                  );
                  dispatch(
                    actions.change(
                      'contact.email',
                      this.props.contact.contactEmail,
                    ),
                  );
                }}
              />
            </div>
          )}
          {this.props.contact.assignedContact && (
            <div className="field is-clearfix">
              <Button
                className="is-danger is-outlined is-pulled-right"
                handleClick={this.props.unsetContact}
              >
                Unset Contact
              </Button>
              <label>Name</label>
              <p>{this.props.contact.assignedContact.name}</p>
              <label>Phone Number</label>
              <p>{this.props.contact.assignedContact.phone_number}</p>
              <label>Email</label>
              <p>
                {this.props.contact.assignedContact.email ||
                  this.props.contact.assignedContact.general_contact_email}
              </p>
            </div>
          )}

          {this.props.contact.contactType &&
            this.props.contact.contactName &&
            !this.props.contact.assignedContact && (
              <Notification type="default">
                <Icon icon="info-circle" />
                &nbsp; You are going to create a new{' '}
                {this.props.contact.contactType} called "
                <strong>{this.props.contact.contactName}</strong>"
              </Notification>
            )}

          {this.props.contact.contacts.length > 0 &&
            !this.props.contact.isLoadingContacts &&
            !this.props.contact.assignedContact && (
              <div className="field">
                <p>
                  <strong>Existing Contacts</strong>
                  <Tooltip text="Choose one of these to re-use contact information">
                    <Icon icon="question-circle" />
                  </Tooltip>
                </p>
                {this.props.contact.contactType === 'driver' && (
                  <DriverList
                    items={this.props.contact.contacts}
                    hideSearch
                    showJoinDate={false}
                    showReputation={true}
                    handleClick={(driver) => {
                      this.props.setContact(driver);
                    }}
                  />
                )}
                {this.props.contact.contactType === 'company' && (
                  <CompanyList
                    items={this.props.contact.contacts}
                    showJoinDate={false}
                    hideSearch
                    handleClick={(company) => {
                      this.props.setContact(company);
                    }}
                  />
                )}
              </div>
            )}

          {/* Loader */}
          {this.props.contact.isLoadingContacts && <Loader fullwidth />}
        </Card>
        <Card>
          <div className="field is-clearfix">
            <Button
              className={`is-primary  is-pulled-right\
              ${this.props.isLoading ? ' is-loading' : ''} ${
                this.props.contact.contactType ? '' : 'is-static'
              }`}
              handleClick={async () => {
                if (
                  await this.saveContact(this.props.contact, this.props.booking)
                ) {
                  await this.props.handleClick();
                }
              }}
            >
              Save and Continue
            </Button>
          </div>
        </Card>
      </div>
    );
  }
}

export default ContactCollectionForm;
